import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { onValue, ref } from 'firebase/database';
import { auth, rtdb } from '../firebase';

/**
 * Auth context scoped to the establishment admin panel.
 *
 * A session is considered valid ONLY if:
 *   - Firebase Auth has a user.
 *   - RTDB `admins/{uid}` exists, has active=true, and role in ['establishment', 'super'].
 *
 * Anyone else is signed out on the spot — Firebase Auth is shared with the
 * mobile app, so a regular student can sign in but must be rejected here.
 */

const Ctx = createContext(null);

export function EstablishmentAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [adminDoc, setAdminDoc] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | anonymous | authorized | denied

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      if (!u) {
        setAdminDoc(null);
        setStatus('anonymous');
      } else {
        setStatus('loading');
      }
    });
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const r = ref(rtdb, `admins/${user.uid}`);
    return onValue(
      r,
      (snap) => {
        const doc = snap.val();
        setAdminDoc(doc || null);
        if (!doc) {
          setStatus('denied');
          return;
        }
        if (doc.active !== true) {
          setStatus('denied');
          return;
        }
        if (doc.role !== 'establishment' && doc.role !== 'super') {
          setStatus('denied');
          return;
        }
        setStatus('authorized');
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[auth] admins read failed', err);
        setStatus('denied');
      }
    );
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      adminDoc,
      status,
      async signIn(email, password) {
        await signInWithEmailAndPassword(auth, email, password);
      },
      async signOut() {
        await signOut(auth);
      }
    }),
    [user, adminDoc, status]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useEstablishmentAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useEstablishmentAuth must be inside EstablishmentAuthProvider');
  return v;
}
